class RRPersonalizationEngine extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.form = this.closest('form[action*="/cart/add"]');
    this.dialog = this.querySelector('[data-rr-dialog]');
    this.textInputs = [...this.querySelectorAll('[data-rr-text-input]')];
    this.textProperties = [...this.querySelectorAll('[data-rr-text-property]')];
    this.previewTexts = [...this.querySelectorAll('[data-rr-preview-text]')];
    this.counts = [...this.querySelectorAll('[data-rr-count]')];
    this.textInput = this.textInputs[0];
    this.textProperty = this.textProperties[0];
    this.previewText = this.previewTexts[0];
    this.count = this.counts[0];
    this.fontProperty = this.querySelector('[data-rr-font-property]');
    if (this.classList.contains('rr-engine--c70') && this.fontProperty) {
      this.fontProperty.value = 'Osaka Chips';
      this.fontProperty.setAttribute('value', 'Osaka Chips');
    }
    this.message = this.querySelector('[data-rr-message]');
    this.addButton = this.querySelector('[data-rr-add]');
    this.openButton = this.querySelector('[data-rr-open]');
    this.closeButtons = this.querySelectorAll('[data-rr-close]');
    this.finishButtons = this.querySelectorAll('[data-rr-finish]');
    this.letterSelect = this.querySelector('[data-rr-letter-select]');
    this.letterProperty = this.querySelector('[data-rr-letter-property]');
    this.sign = this.querySelector('.rr-engine__sign');
    this.productSubmit = this.form?.querySelector('[type="submit"][name="add"]');
    this.variantIdInput = this.form?.querySelector('input[name="id"]');
    this.maxLength = Number(this.textInput?.maxLength || 24);
    this.lastFocus = null;

    this.openButton?.addEventListener('click', () => this.open());
    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen()) this.close();
    });

    this.textInputs.forEach((input, index) => input.addEventListener('input', () => this.updateText(index)));
    this.finishButtons.forEach((button) => button.addEventListener('click', () => this.selectFinish(button)));
    this.letterSelect?.addEventListener('change', () => this.updateLetter());
    this.addButton?.addEventListener('click', () => this.addToCart());

    this.availabilityObserver = new MutationObserver(() => this.updateValidity());
    if (this.productSubmit) this.availabilityObserver.observe(this.productSubmit, { attributes: true, attributeFilter: ['disabled'] });
    if (this.variantIdInput) this.availabilityObserver.observe(this.variantIdInput, { attributes: true, attributeFilter: ['disabled', 'value'] });

    this.variantRoot = this.closest('product-info') || document;
    this.variantRoot.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"], select')) this.syncFinishFromVariant();
    });

    this.syncFinishFromVariant();
    this.updateLetter();
    this.updateText();
    if (this.classList.contains('rr-engine--f13')) {
      this.f13ResizeObserver = new ResizeObserver(() => this.fitText());
      const stage = this.querySelector('.rr-engine__stage');
      if (stage) this.f13ResizeObserver.observe(stage);
      document.fonts?.ready.then(() => this.fitText());
    }
  }

  disconnectedCallback() {
    this.f13ResizeObserver?.disconnect();
  }

  isOpen() {
    return this.dialog && !this.dialog.hidden;
  }

  open() {
    if (!this.dialog) return;
    this.lastFocus = document.activeElement;
    this.dialog.hidden = false;
    document.documentElement.classList.add('rr-personalization-open');
    requestAnimationFrame(() => {
      this.textInput?.focus();
      if (this.classList.contains('rr-engine--f13')) this.fitText();
    });
  }

  close() {
    if (!this.dialog) return;
    this.dialog.hidden = true;
    document.documentElement.classList.remove('rr-personalization-open');
    this.lastFocus?.focus?.();
  }

  normalizeText(value) {
    const mode = this.dataset.textCase || 'preserve';
    const trimmed = String(value || '').slice(0, this.maxLength);
    return mode === 'uppercase' ? trimmed.toUpperCase() : trimmed;
  }

  updateText(index) {
    const indexes = Number.isInteger(index) ? [index] : this.textInputs.map((_, i) => i);
    indexes.forEach((i) => {
      const input = this.textInputs[i];
      if (!input) return;
      const maxLength = Number(input.maxLength || 24);
      const value = this.normalizeText(input.value).slice(0, maxLength);
      if (input.value !== value) input.value = value;
      if (this.textProperties[i]) {
        this.textProperties[i].value = value;
        this.textProperties[i].setAttribute('value', value);
      }
      if (this.previewTexts[i]) {
        const preview = this.previewTexts[i];
        const isQ22 = this.classList.contains('rr-engine--q22');
        const isB26 = this.classList.contains('rr-engine--b26');
        const usesArchedTop = this.classList.contains('rr-engine--q9') || this.classList.contains('rr-engine--e15') || isQ22 || isB26;
        if (usesArchedTop && i === 0 && value) {
          const characters = Array.from(value);
          const lastIndex = Math.max(characters.length - 1, 1);
          preview.replaceChildren(...characters.map((character, characterIndex) => {
            const span = document.createElement('span');
            span.className = 'rr-engine__arched-character';
            span.textContent = character === ' ' ? '\u00a0' : character;
            const normalizedPosition = (characterIndex / lastIndex) * 2 - 1;
            const archDepth = isQ22 ? -0.78 : isB26 ? -0.42 : -0.38;
            const rise = archDepth * (1 - normalizedPosition * normalizedPosition);
            span.style.setProperty('--rr-arch-y', `${rise.toFixed(3)}em`);
            if (isQ22 || isB26) {
              const rotation = normalizedPosition * (isQ22 ? 5 : 4);
              span.style.setProperty('--rr-arch-rotate', `${rotation.toFixed(2)}deg`);
            }
            if (isQ22) {
              const rotation = normalizedPosition * 5;
              span.style.position = 'relative';
              span.style.top = `${rise.toFixed(3)}em`;
              span.style.transform = `rotate(${rotation.toFixed(2)}deg)`;
            }
            return span;
          }));
        } else {
          preview.textContent = value;
        }
        preview.classList.toggle('is-empty', !value);
      }
      if (this.counts[i]) this.counts[i].textContent = `${value.length} / ${maxLength}`;
      this.fitText(i);
    });
    this.updateValidity();
  }

  fitText(index = 0) {
    const previewText = this.previewTexts[index];
    if (!previewText) return;
    const scaleY = Number(this.dataset.textScaleY || 1);
    const widthKey = index === 0 ? 'textWidthTop' : 'textWidthBottom';
    const widthFactor = Number(this.dataset[widthKey] || this.dataset.textWidth || 0.62);
    const translateY = (this.classList.contains('rr-engine--j18') || this.classList.contains('rr-engine--o8')) ? '-100%' : '-50%';
    previewText.style.transform = `translate(-50%, ${translateY}) scaleX(1) scaleY(${scaleY})`;
    const available = previewText.parentElement.clientWidth * widthFactor;
    const actual = previewText.scrollWidth;
    const scale = actual > available && actual > 0 ? Math.max(this.classList.contains('rr-engine--f13') ? 0 : 0.42, available / actual) : 1;
    previewText.style.transform = `translate(-50%, ${translateY}) scaleX(${scale}) scaleY(${scaleY})`;
  }

  updateLetter() {
    if (!this.letterSelect) return;
    const option = this.letterSelect.selectedOptions[0];
    const letter = this.letterSelect.value || '';
    if (this.letterProperty) this.letterProperty.value = letter;
    if (letter && option?.dataset.mask && this.sign) this.sign.style.setProperty('--rr-mask', `url("${option.dataset.mask}")`);
    this.updateValidity();
  }

  selectFinish(button) {
    if (!button) return;
    const finish = button.dataset.rrFinish;
    const variantControl = this.findVariantControl(finish);

    if (this.classList.contains('rr-engine--a35') && variantControl) {
      variantControl.checked = true;
      const productRoot = this.closest('product-info') || document;
      const variantSelector = productRoot.querySelector('variant-selects, variant-radios');
      const selectedOptions = [...(variantSelector?.querySelectorAll('fieldset') || [])]
        .map((fieldset) => fieldset.querySelector('input[type="radio"]:checked')?.value)
        .filter(Boolean);
      let selectedVariant = null;
      try {
        const variants = JSON.parse(this.querySelector('[data-rr-variant-map]')?.textContent || '[]');
        selectedVariant = variants.find((variant) =>
          variant.options.every((option, index) => option === selectedOptions[index])
        );
      } catch (error) {}
      if (selectedVariant) {
        productRoot.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
          input.value = String(selectedVariant.id);
          input.removeAttribute('disabled');
        });
        if (this.variantIdInput) {
          this.variantIdInput.value = String(selectedVariant.id);
          this.variantIdInput.removeAttribute('disabled');
        }
      }
      this.applyFinish(finish);
      this.updateText();
      return;
    }

    if (variantControl) {
      if (variantControl.tagName === 'SELECT') {
        variantControl.value = finish;
      } else {
        variantControl.checked = true;
      }
      variantControl.dispatchEvent(new Event('change', { bubbles: true }));
      variantControl.dispatchEvent(new Event('input', { bubbles: true }));
      window.setTimeout(() => this.updateValidity(), 500);
    }
    this.applyFinish(finish);
  }

  findVariantControl(finish) {
    if (!finish) return null;
    const productRoot = this.closest('product-info') || document;
    const controls = [...productRoot.querySelectorAll('variant-selects input[type="radio"], variant-radios input[type="radio"], variant-selects select, variant-radios select, [data-option-name] input[type="radio"]')];
    return controls.find((control) => {
      if (control.tagName === 'SELECT') {
        return [...control.options].some((option) => option.value === finish);
      }
      return control.value === finish;
    }) || null;
  }

  currentVariantFinish() {
    const productRoot = this.closest('product-info') || document;
    const selected = [...productRoot.querySelectorAll('variant-selects input[type="radio"]:checked, variant-radios input[type="radio"]:checked, variant-selects select, variant-radios select, [data-option-name] input[type="radio"]:checked')];
    for (const control of selected) {
      const value = control.tagName === 'SELECT' ? control.value : control.value;
      if ([...this.finishButtons].some((button) => button.dataset.rrFinish === value)) return value;
    }
    return this.finishButtons[0]?.dataset.rrFinish || '';
  }

  syncFinishFromVariant() {
    this.applyFinish(this.currentVariantFinish());
  }

  applyFinish(finish) {
    this.dataset.selectedFinish = finish || '';
    this.finishButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.rrFinish === finish)));
    this.updateValidity();
  }

  updateValidity() {
    const hasText = this.textProperties.length > 0 && this.textProperties.every((field) => Boolean(field.value.trim()));
    const hasFinish = Boolean(this.dataset.selectedFinish);
    const hasLetter = !this.letterSelect || Boolean(this.letterProperty?.value);
    const available = !this.productSubmit?.disabled;
    const valid = hasText && hasLetter && hasFinish && available;
    if (this.addButton) this.addButton.disabled = !valid;
    if (this.message) {
      this.message.textContent = !hasLetter
        ? 'Choose your monogram letter to continue.'
        : !hasText
          ? 'Enter your custom text to continue.'
          : !hasFinish
          ? 'Choose a finish to continue.'
          : !available
            ? 'This selection is currently unavailable.'
            : 'Ready to add to cart.';
    }
  }

  syncFormState() {
    this.form = this.closest('form[action*="/cart/add"]');
    this.productSubmit = this.form?.querySelector('[type="submit"][name="add"]');
    this.variantIdInput = this.form?.querySelector('input[name="id"]');

    const productRoot = this.closest('product-info') || document;
    const variantScripts = [...productRoot.querySelectorAll('script[data-selected-variant]')];
    for (const script of variantScripts.reverse()) {
      try {
        const variant = JSON.parse(script.textContent || '{}');
        if (variant?.id && this.variantIdInput) {
          this.variantIdInput.value = String(variant.id);
          this.variantIdInput.setAttribute('value', String(variant.id));
          this.variantIdInput.removeAttribute('disabled');
          break;
        }
      } catch (error) {}
    }

    this.textProperties.forEach((field) => field.setAttribute('value', field.value));
    if (this.fontProperty) this.fontProperty.setAttribute('value', this.fontProperty.value);
  }

  addToCart() {
    this.updateText();
    this.syncFormState();
    this.updateValidity();
    if (this.addButton?.disabled || !this.productSubmit) return;
    this.addButton.disabled = true;
    if (this.message) this.message.textContent = 'Adding to cart…';
    this.close();
    this.productSubmit.click();
  }
}

if (!customElements.get('rr-personalization-engine')) {
  customElements.define('rr-personalization-engine', RRPersonalizationEngine);
}
